import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';
import { SEARCH_INPUT_PROPS } from '@/constants';

export interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /**
   * `field` — global `.field` look (forms, admin tables);
   * `pill` — rounded, transparent filter input (list toolbars).
   */
  variant?: 'field' | 'pill';
  /**
   * `compact` — header height (36px), filled, stretches and shrinks with the
   * parent (`min-width: 0`).
   */
  density?: 'default' | 'compact';
  /** Enter / the keyboard "Search" key. */
  onSubmit?: (value: string) => void;
  testId?: string;
  className?: string;
}

/**
 * Search input with a leading icon and a clear button. Clearing returns focus
 * to the input so the user can type the next query straight away.
 */
export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(({
  value,
  onChange,
  placeholder,
  variant = 'field',
  density = 'default',
  onSubmit,
  testId,
  className,
}, ref) => {
  const { t } = useTranslation('common');
  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);
  const isPill = variant === 'pill';

  const clear = () => {
    onChange('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (onSubmit && e.key === 'Enter' && !e.nativeEvent.isComposing) {
      e.preventDefault();
      onSubmit(e.currentTarget.value);
    }
  };

  return (
    <div
      className={[
        styles.wrapper,
        isPill && styles.pill,
        density === 'compact' && styles.compact,
        className,
      ].filter(Boolean).join(' ')}
    >
      <i className={`icon-search ${styles.icon}`} aria-hidden="true" />
      <input
        ref={inputRef}
        type="search"
        className={isPill ? styles.input : `field ${styles.input}`}
        enterKeyHint="search"
        autoComplete="off"
        {...SEARCH_INPUT_PROPS}
        placeholder={placeholder}
        aria-label={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        data-testid={testId}
      />
      {value && (
        <button
          type="button"
          className={styles.clear}
          onClick={clear}
          aria-label={t('clear')}
          data-testid={testId ? `${testId}-clear` : undefined}
        >
          <i className="icon-xmark" aria-hidden="true" />
        </button>
      )}
    </div>
  );
});

SearchField.displayName = 'SearchField';

export default SearchField;
